/**
 * Home page editorial content (EN / FR).
 *
 * Single source of truth shared by:
 *   - src/pages/HomePage.tsx              (React, client render)
 *   - scripts/render_static_page_body.mjs (pre-rendered static HTML)
 *
 * Rules:
 *   - No invented data or statistics. No "verified" claims.
 *   - Keep EN and FR structurally identical (same sections, same link targets).
 *   - Paragraphs are arrays of segments: a plain string, or { text, href } for an
 *     internal link. Anchor text must describe the destination (never "click here").
 *   - hrefs are the canonical, pre-rendered URLs (see scripts/generate_static_seo_pages.mjs).
 *
 * TODO(land-prices): add a "Land prices by city" H2 section here ONLY once we have
 * sourced, dated price-per-m² data (source name + publication date per city).
 * The current priceRange values in scripts/neighborhood_seo_data.mjs have no
 * priceSource, so they must not be presented as market data on the home page.
 */

const city = (lang, slug) => (lang === 'fr' ? `/fr/villes/${slug}` : `/en/cities/${slug}`);

const cityLinks = (lang) => [
  { text: 'Douala', href: city(lang, 'douala') },
  ', ',
  { text: 'Yaoundé', href: city(lang, 'yaounde') },
  ', ',
  { text: 'Kribi', href: city(lang, 'kribi') },
  ', ',
  { text: 'Buea', href: city(lang, 'buea') },
  ', ',
  { text: lang === 'fr' ? 'Limbé' : 'Limbe', href: city(lang, 'limbe') },
  ', ',
  { text: 'Bafoussam', href: city(lang, 'bafoussam') },
  ', ',
  { text: 'Bamenda', href: city(lang, 'bamenda') },
  lang === 'fr' ? ' et ' : ' and ',
  { text: 'Garoua', href: city(lang, 'garoua') },
];

export const HOME_SEO_CONTENT = {
  en: {
    sections: [
      {
        id: 'about',
        heading: 'Land and property for sale across Cameroon',
        paragraphs: [
          [
            'Cameroon Land Market is a free classifieds site where owners, agents and buyers list land, houses, apartments and commercial property across all 10 regions of Cameroon. Browse plots in ',
            ...cityLinks('en'),
            ', compare prices per square metre, and learn how to check a Titre Foncier before you pay. Posting a listing is free, and there is no broker commission.',
          ],
          [
            'You will find titled plots, family homes, apartments to rent, shops and offices. Each listing shows the asking price in CFA francs (XAF), the neighbourhood, photos and the seller’s contact details, so you deal directly with the owner or agent by phone or WhatsApp.',
          ],
        ],
        subsections: [],
      },
      {
        id: 'buy-safely',
        heading: 'How to buy land safely in Cameroon',
        paragraphs: [
          [
            'Common pitfalls include a plot sold to more than one buyer, a seller who is not the registered owner, and land without a valid title. Three precautions reduce these risks.',
          ],
        ],
        subsections: [
          {
            heading: 'Check the Titre Foncier',
            paragraphs: [
              [
                'Ask the seller for the land title number and have it checked at the land registry (Conservation Foncière) of the division where the plot is located. The name on the title should match the seller, and the title should be free of disputes or charges.',
              ],
            ],
          },
          {
            heading: 'Have the boundaries surveyed',
            paragraphs: [
              [
                'Hire a sworn surveyor (géomètre assermenté) to locate the boundary markers on site and confirm that the plot matches the area and plan shown on the title.',
              ],
            ],
          },
          {
            heading: 'Sign the sale before a notary',
            paragraphs: [
              [
                'Complete the purchase with a notarial deed, then register the transfer so the title is issued in your name. Do not hand over the full price in cash before these steps are done.',
              ],
            ],
          },
        ],
        links: [
          { text: 'Read the complete land buying guide for Cameroon', href: '/en/resources/land-buying-guide' },
          { text: 'Review our anti-scam checklist for property buyers', href: '/en/resources/anti-scam-checklist' },
        ],
      },
      {
        id: 'diaspora',
        heading: 'For diaspora buyers',
        paragraphs: [
          [
            'Buying from Europe, North America or elsewhere in Africa? You can browse listings and contact sellers online, but do not sign or pay for anything until the land has been checked on the ground. Appoint someone you trust with a written power of attorney, use independent professionals rather than the seller’s contacts, and pay by traceable bank transfer instead of cash handed to intermediaries.',
          ],
        ],
        subsections: [],
        links: [
          { text: 'Read our guide to buying property in Cameroon from abroad', href: '/en/resources/diaspora-investment' },
        ],
      },
      {
        id: 'sell',
        heading: 'Selling or renting out property?',
        paragraphs: [
          [
            'Owners and agents can publish land, houses, apartments and commercial premises with photos, price and location, and receive enquiries from buyers and tenants directly.',
          ],
        ],
        subsections: [],
        links: [{ text: 'Post your property listing for free', href: '/en/post-property' }],
      },
    ],
  },

  fr: {
    sections: [
      {
        id: 'about',
        heading: 'Terrains et biens immobiliers à vendre partout au Cameroun',
        paragraphs: [
          [
            'Cameroon Land Market est un site de petites annonces gratuites où propriétaires, agents et acheteurs publient terrains, maisons, appartements et locaux commerciaux dans les 10 régions du pays. Parcourez les parcelles proposées à ',
            ...cityLinks('fr'),
            ', comparez les prix au mètre carré et apprenez à vérifier un titre foncier avant de verser le moindre franc. La publication est gratuite et aucune commission d’intermédiaire n’est prélevée.',
          ],
          [
            'Vous y trouverez des terrains titrés, des maisons familiales, des appartements à louer, des boutiques et des bureaux. Chaque annonce indique le prix demandé en francs CFA (XAF), le quartier, des photos et les coordonnées du vendeur : vous échangez directement avec le propriétaire ou l’agent, par téléphone ou sur WhatsApp.',
          ],
        ],
        subsections: [],
      },
      {
        id: 'buy-safely',
        heading: 'Comment acheter un terrain en toute sécurité au Cameroun',
        paragraphs: [
          [
            'Parmi les pièges courants : un terrain vendu à plusieurs acheteurs, un vendeur qui n’est pas le propriétaire inscrit, ou une parcelle sans titre foncier valable. Trois précautions limitent ces risques.',
          ],
        ],
        subsections: [
          {
            heading: 'Vérifier le titre foncier',
            paragraphs: [
              [
                'Demandez au vendeur le numéro du titre foncier et faites-le vérifier à la Conservation foncière du département où se situe le terrain. Le nom inscrit sur le titre doit correspondre à celui du vendeur, et le titre ne doit faire l’objet d’aucun litige ni d’aucune charge.',
              ],
            ],
          },
          {
            heading: 'Faire borner le terrain',
            paragraphs: [
              [
                'Faites appel à un géomètre assermenté pour retrouver les bornes sur place et confirmer que la parcelle correspond à la superficie et au plan figurant sur le titre.',
              ],
            ],
          },
          {
            heading: 'Signer la vente chez le notaire',
            paragraphs: [
              [
                'Concluez l’achat par un acte notarié, puis faites enregistrer la mutation pour que le titre soit établi à votre nom. Ne remettez pas la totalité du prix en espèces avant d’avoir franchi ces étapes.',
              ],
            ],
          },
        ],
        links: [
          { text: 'Lire le guide complet pour acheter un terrain au Cameroun', href: '/fr/ressources/guide-achat-terrain' },
          { text: 'Consulter notre checklist anti-arnaque pour les acheteurs', href: '/fr/ressources/guide-anti-arnaque' },
        ],
      },
      {
        id: 'diaspora',
        heading: 'Acheteurs de la diaspora',
        paragraphs: [
          [
            'Vous vivez en Europe, en Amérique du Nord ou ailleurs en Afrique ? Vous pouvez consulter les annonces et contacter les vendeurs en ligne, mais ne signez et ne payez rien tant que le terrain n’a pas été vérifié sur place. Confiez une procuration écrite à une personne de confiance, faites appel à des professionnels indépendants plutôt qu’aux contacts du vendeur, et préférez un virement bancaire traçable aux espèces remises à des intermédiaires.',
          ],
        ],
        subsections: [],
        links: [
          { text: 'Lire notre guide pour acheter au Cameroun depuis l’étranger', href: '/fr/ressources/investissement-diaspora' },
        ],
      },
      {
        id: 'sell',
        heading: 'Vous vendez ou louez un bien ?',
        paragraphs: [
          [
            'Propriétaires et agents peuvent publier terrains, maisons, appartements et locaux commerciaux avec photos, prix et localisation, et recevoir directement les demandes des acheteurs et des locataires.',
          ],
        ],
        subsections: [],
        links: [{ text: 'Déposer gratuitement votre annonce immobilière', href: '/fr/deposer-annonce' }],
      },
    ],
  },
};

/** Returns the content for a language ('en' | 'fr'), defaulting to English. */
export function getHomeSeoContent(lang) {
  return lang === 'fr' ? HOME_SEO_CONTENT.fr : HOME_SEO_CONTENT.en;
}

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const segmentsToHtml = (segments, linkClass) =>
  segments
    .map((seg) =>
      typeof seg === 'string'
        ? escapeHtml(seg)
        : `<a href="${escapeHtml(seg.href)}" class="${linkClass}">${escapeHtml(seg.text)}</a>`
    )
    .join('');

/**
 * Static HTML renderer for the pre-rendered home page. Markup and Tailwind
 * classes mirror the <HomeSeoContent /> React component exactly.
 */
export function renderHomeSeoContentHtml(lang) {
  const { sections } = getHomeSeoContent(lang);
  const isFr = lang === 'fr';
  const inlineLink = 'text-[#094426] font-semibold underline underline-offset-2 hover:text-[#07361e]';
  const ctaLink = 'inline-flex items-center gap-1.5 text-sm font-bold text-[#094426] hover:underline';

  const sectionHtml = sections
    .map(
      (s) => `
        <div id="home-${s.id}" class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-6 flex flex-col justify-between space-y-4 hover:border-emerald-200 transition-colors">
          <div class="space-y-3">
            <h3 id="home-${s.id}-heading" class="text-lg sm:text-xl font-serif font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-[#094426] shrink-0"></span>
              <span>${escapeHtml(s.heading)}</span>
            </h3>
            ${s.paragraphs.map((p) => `<p class="text-slate-600 text-xs sm:text-sm leading-relaxed">${segmentsToHtml(p, inlineLink)}</p>`).join('\n')}
            ${(s.subsections || [])
              .map(
                (sub) => `
            <div class="pt-2 border-t border-slate-200/60 mt-2">
              <h4 class="text-xs sm:text-sm font-bold text-slate-900 flex items-center gap-1.5">
                <span class="text-emerald-700">✓</span>
                <span>${escapeHtml(sub.heading)}</span>
              </h4>
              ${sub.paragraphs.map((p) => `<p class="mt-1 text-slate-600 text-xs sm:text-sm leading-relaxed">${segmentsToHtml(p, inlineLink)}</p>`).join('\n')}
            </div>`
              )
              .join('')}
          </div>
          ${(s.links || []).length
            ? `<div class="pt-3 border-t border-slate-200/60"><ul class="space-y-1.5">${s.links.map((l) => `<li><a href="${escapeHtml(l.href)}" class="${ctaLink}"><span>${escapeHtml(l.text)}</span> <span>→</span></a></li>`).join('')}</ul></div>`
            : ''}
        </div>`
    )
    .join('\n');

  return `
        <!-- Home editorial content (shared source: src/content/homeSeoContent.mjs) -->
        <section class="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div class="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-xs">
            <div class="max-w-3xl mb-8">
              <div class="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#094426] mb-2">
                <span class="w-2 h-2 rounded-full bg-[#094426]"></span>
                <span>${isFr ? 'Sécurité Juridique & Cadastre' : 'Legal Framework & Land Registry'}</span>
              </div>
              <h2 class="text-2xl sm:text-3xl font-serif font-bold text-slate-900 tracking-tight">
                ${isFr ? 'Guide Foncier et Pratiques Notariales au Cameroun' : 'Cameroon Real Estate & Notary Legal Guide'}
              </h2>
              <p class="text-slate-600 text-sm sm:text-base leading-relaxed mt-1">
                ${isFr ? 'Procédures MINDCAF, vérification du Titre Foncier, bornage contradictoire et sécurité des transactions sur les 10 régions.' : 'Official MINDCAF procedures, land title deed verification, boundary surveys, and safe transaction guidelines across all 10 regions.'}
              </p>
            </div>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
              ${sectionHtml}
            </div>
          </div>
        </section>`;
}

